// WooCommerce API Service
// Note: Credentials are stored securely on the backend.
// The frontend uses a proxy API route to communicate with WooCommerce.

interface WooCommerceCoupon {
  id: number;
  code: string;
  amount: string;
  discount_type: string;
  description: string;
  date_expires: string | null;
  usage_count: number;
  individual_use: boolean;
  product_ids: number[];
  excluded_product_ids: number[];
  usage_limit: number | null;
  usage_limit_per_user: number | null;
  limit_usage_to_x_items: number | null;
  free_shipping: boolean;
  product_categories: number[];
  excluded_product_categories: number[];
  exclude_sale_items: boolean;
  minimum_amount: string;
  maximum_amount: string;
  email_restrictions: string[];
  meta_data: Record<string, unknown>[];
  date_created: string;
  date_created_gmt: string;
  date_modified: string;
  date_modified_gmt: string;
}

interface WooCommerceProduct {
  id: number;
  name: string;
  slug: string;
  permalink: string;
  date_created: string;
  date_created_gmt: string;
  date_modified: string;
  date_modified_gmt: string;
  type: string;
  status: string;
  featured: boolean;
  catalog_visibility: string;
  description: string;
  short_description: string;
  sku: string;
  price: string;
  regular_price: string;
  sale_price: string;
  date_on_sale_from: string | null;
  date_on_sale_from_gmt: string | null;
  date_on_sale_to: string | null;
  date_on_sale_to_gmt: string | null;
  on_sale: boolean;
  purchasable: boolean;
  total_sales: number;
  virtual: boolean;
  downloadable: boolean;
  downloads: Record<string, unknown>[];
  download_limit: number;
  download_expiry: number;
  tax_status: string;
  tax_class: string;
  manage_stock: boolean;
  stock_quantity: number | null;
  stock_status: string;
  backorders: string;
  backorders_allowed: boolean;
  backordered: boolean;
  sold_individually: boolean;
  weight: string;
  dimensions: {
    length: string;
    width: string;
    height: string;
  };
  shipping_required: boolean;
  shipping_taxable: boolean;
  shipping_class: string;
  shipping_class_id: number;
  reviews_allowed: boolean;
  average_rating: string;
  rating_count: number;
  images: Record<string, unknown>[];
  categories: Record<string, unknown>[];
  tags: Record<string, unknown>[];
  attributes: Record<string, unknown>[];
  default_attributes: Record<string, unknown>[];
  variations: Record<string, unknown>[];
  grouped_products: Record<string, unknown>[];
  menu_order: number;
  meta_data: Record<string, unknown>[];
}

interface WooCommerceOrder {
  id: number;
  parent_id: number;
  number: string;
  order_key: string;
  created_via: string;
  version: string;
  status: string;
  currency: string;
  date_created: string;
  date_created_gmt: string;
  date_modified: string;
  date_modified_gmt: string;
  discount_total: string;
  discount_tax: string;
  shipping_total: string;
  shipping_tax: string;
  cart_tax: string;
  total: string;
  total_tax: string;
  prices_include_tax: boolean;
  customer_id: number;
  customer_ip_address: string;
  customer_user_agent: string;
  customer_note: string;
  billing: {
    first_name: string;
    last_name: string;
    company: string;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
    email: string;
    phone: string;
  };
  shipping: {
    first_name: string;
    last_name: string;
    company: string;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
  };
  payment_method: string;
  payment_method_title: string;
  transaction_id: string;
  date_paid: string | null;
  date_paid_gmt: string | null;
  date_completed: string | null;
  date_completed_gmt: string | null;
  cart_hash: string;
  meta_data: Record<string, unknown>[];
  line_items: Record<string, unknown>[];
  tax_lines: Record<string, unknown>[];
  shipping_lines: Record<string, unknown>[];
  fee_lines: Record<string, unknown>[];
  coupon_lines: Record<string, unknown>[];
  refunds: Record<string, unknown>[];
}

interface WooCommerceCouponTotals {
  total_discount?: string;
  total_orders?: number;
  total_coupons?: number;
  avg_discount?: string;
  period?: string;
  date_start?: string;
  date_end?: string;
  // Alternative field names that WooCommerce might use
  discount_total?: string;
  orders_count?: number;
  coupons_count?: number;
  average_discount?: string;
}

class WooCommerceAPI {
  constructor() {
    // No credentials needed in frontend - backend proxy handles authentication
  }

  private async makeRequest(endpoint: string, method: string = "GET", body?: Record<string, unknown>) {
    // Use our backend proxy instead of calling WooCommerce directly
    const url = `/api/${endpoint}`;

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    const config: RequestInit = {
      method,
      headers,
    };

    if (body && method !== "GET") {
      config.body = JSON.stringify(body);
    }

    try {
      const { apiFetch } = await import("./api");
      const response = await apiFetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `WooCommerce API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("WooCommerce API request failed:", error);
      throw error;
    }
  }

  // Coupons
  async getCoupons(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    after?: string;
    before?: string;
    exclude?: number[];
    include?: number[];
    offset?: number;
    order?: "asc" | "desc";
    orderby?: string;
    code?: string;
  }): Promise<WooCommerceCoupon[]> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            queryParams.append(key, value.join(","));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const endpoint = `coupons${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.makeRequest(endpoint);
  }

  async getCoupon(id: number): Promise<WooCommerceCoupon> {
    return this.makeRequest(`coupons/${id}`);
  }

  async createCoupon(couponData: Partial<WooCommerceCoupon>): Promise<WooCommerceCoupon> {
    return this.makeRequest("coupons", "POST", couponData);
  }

  async updateCoupon(id: number, couponData: Partial<WooCommerceCoupon>): Promise<WooCommerceCoupon> {
    return this.makeRequest(`coupons/${id}`, "PUT", couponData);
  }

  async deleteCoupon(id: number): Promise<void> {
    return this.makeRequest(`coupons/${id}`, "DELETE");
  }

  // Products
  async getProducts(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    after?: string;
    before?: string;
    exclude?: number[];
    include?: number[];
    offset?: number;
    order?: "asc" | "desc";
    orderby?: string;
    slug?: string;
    status?: string;
    type?: string;
    sku?: string;
    featured?: boolean;
    category?: string;
    tag?: string;
    shipping_class?: string;
    attribute?: string;
    attribute_term?: string;
    tax_class?: string;
    on_sale?: boolean;
    min_price?: string;
    max_price?: string;
    stock_status?: string;
  }): Promise<WooCommerceProduct[]> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            queryParams.append(key, value.join(","));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const endpoint = `products${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.makeRequest(endpoint);
  }

  async getProduct(id: number): Promise<WooCommerceProduct> {
    return this.makeRequest(`products/${id}`);
  }

  // Orders
  async getOrders(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    after?: string;
    before?: string;
    exclude?: number[];
    include?: number[];
    offset?: number;
    order?: "asc" | "desc";
    orderby?: string;
    parent?: number[];
    parent_exclude?: number[];
    status?: string[];
    customer?: number;
    product?: number;
    dp?: number;
  }): Promise<WooCommerceOrder[]> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            queryParams.append(key, value.join(","));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const endpoint = `orders${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.makeRequest(endpoint);
  }

  async getOrder(id: number): Promise<WooCommerceOrder> {
    return this.makeRequest(`orders/${id}`);
  }

  // Test connection
  async testConnection(): Promise<boolean> {
    try {
      await this.makeRequest("products?per_page=1");
      return true;
    } catch (error) {
      console.error("WooCommerce connection test failed:", error);
      return false;
    }
  }

  // Coupon Reports
  async getCouponTotals(params?: {
    date_min?: string;
    date_max?: string;
    period?: "day" | "week" | "month" | "quarter" | "year";
  }): Promise<WooCommerceCouponTotals[]> {
    const queryParams = new URLSearchParams();

    if (params?.date_min) queryParams.append("date_min", params.date_min);
    if (params?.date_max) queryParams.append("date_max", params.date_max);
    if (params?.period) queryParams.append("period", params.period);

    const endpoint = `reports/coupons/totals${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.makeRequest(endpoint);
  }
}

export const woocommerceAPI = new WooCommerceAPI();
export type { WooCommerceCoupon, WooCommerceProduct, WooCommerceOrder, WooCommerceCouponTotals };
